import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';

import { useThemeStore } from '../state/themeStore';
import { SUPPORTED_LANGUAGES, setLanguage } from '../i18n';
import { BrandMark } from './BrandMark';
import { IconBell, IconCheck, IconMoon, IconStack, IconSun } from './landing/icons';

/**
 * Minimal shell for pages reached before there is an account to navigate: sign-in, registration,
 * password reset and 404. These must not render the app sidebar — its destinations all require a
 * session, so a signed-out visitor would see six links that only bounce them back here.
 *
 * Two panes. The form has the left one to itself; the right one says what the product is for, for
 * the visitor who arrived from an ad or an email and has never seen the inside of it. It carries no
 * figures and no quotation: a number on a sign-in page reads as the visitor's own, and we have no
 * customer whose words we could honestly put there.
 */
export default function AuthLayout({ children }: { children: ReactNode }) {
  const { t, i18n } = useTranslation();
  const theme = useThemeStore((s) => s.theme);
  const toggleTheme = useThemeStore((s) => s.toggle);

  return (
    <div className="auth">
      <div className="auth__pane">
        <header className="auth__bar">
          {/* The brand below links home too, but nothing about it says so, and without this the
              sign-in page was a dead end for anyone who opened it by mistake or changed their mind. */}
          <Link className="auth__back" to={i18n.resolvedLanguage === 'en' ? '/en' : '/'}>
            ← {t('auth.backHome')}
          </Link>
          <div className="toolbar">
            <select
              className="btn btn--small"
              aria-label={t('language.label')}
              value={i18n.resolvedLanguage}
              onChange={(e) => void setLanguage(e.target.value)}
            >
              {SUPPORTED_LANGUAGES.map((lng) => (
                <option key={lng} value={lng}>
                  {t(`language.${lng}`)}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn btn--small btn--icon"
              onClick={toggleTheme}
              aria-label={theme === 'dark' ? t('theme.light') : t('theme.dark')}
            >
              {theme === 'dark' ? <IconSun size={16} /> : <IconMoon size={16} />}
            </button>
          </div>
        </header>

        <main className="auth__main">
          {/* With the form rather than in the corner: it is the first line of what the visitor is
              about to sign in to, and up there it was a second, unrelated thing to look at. */}
          <Link className="brand auth__brand" to="/">
            <BrandMark size={32} />
            {t('app.name')}
          </Link>

          {children}
        </main>

        <p className="auth__disclaimer">{t('app.disclaimer')}</p>
      </div>

      <aside className="auth__aside">
        {/* Decoration: three cards from the product, drawn rather than screenshotted so they follow
            the language and never go stale. Hidden from assistive tech — the sentences under them
            say the same thing in words. */}
        <div className="auth__scene" aria-hidden="true">
          <span className="auth__orb auth__orb--a" />
          <span className="auth__orb auth__orb--b" />

          <div className="auth__float auth__float--deadline">
            <span className="auth__float-icon">
              <IconBell size={16} />
            </span>
            <span>
              <strong>{t('auth.aside.cardDeadline')}</strong>
              <small>{t('auth.aside.cardDeadlineSub')}</small>
            </span>
          </div>

          <div className="auth__float auth__float--chart">
            <small>{t('auth.aside.cardYear')}</small>
            <span className="auth__bars">
              {Array.from({ length: 8 }, (_, i) => (
                <i key={i} />
              ))}
            </span>
          </div>

          <div className="auth__float auth__float--ready">
            <span className="auth__float-icon">
              <IconStack size={16} />
            </span>
            <span>
              <strong>{t('auth.aside.cardDeclaration')}</strong>
              <small>{t('auth.aside.cardDeclarationSub')}</small>
            </span>
            <span className="auth__tick">
              <IconCheck size={13} />
            </span>
          </div>
        </div>

        <div className="auth__pitch">
          <h2>{t('auth.aside.title')}</h2>
          <ul>
            {(['deadlines', 'figures', 'data'] as const).map((key) => (
              <li key={key}>
                <IconCheck size={16} />
                {t(`auth.aside.points.${key}`)}
              </li>
            ))}
          </ul>
        </div>
      </aside>
    </div>
  );
}
